// Adresse d'alerte (décision O107, chemin B) : une adresse de courriel destinataire des alertes de sécurité d'une personne,
// retenue soit par le fournisseur d'identité (revendication `email` vérifiée, d'un domaine du client), soit déclarée par
// un administrateur et confirmée par la personne avec un code reçu à cette adresse. Libellés, résumé de l'état annoncé
// par GET /v0/moi, règles d'affichage des actions. Le code de confirmation n'existe que dans le courriel : rien ici ne le
// fabrique, ne le rend ni ne le garde.
import type { EtatAdresse, EtatAdresseAlerte, VueAdresse } from '../api/types';
import { codeVerificationValide, normaliserCodeVerification } from './webauthn';

export const LIBELLE_ETAT_ADRESSE: Record<EtatAdresse, string> = {
  declaree: 'Déclarée : attend la confirmation',
  expiree: 'Expirée (72 heures sans confirmation)',
  en_carence: 'Confirmée, en délai de sécurité',
  confirmee: 'Confirmée, en service',
  remplacee: 'Remplacée',
  desactivee: 'Désactivée',
  annulee: 'Annulée',
  invalide: 'Inutilisable : état altéré',
};

export const ETATS_ADRESSE: readonly EtatAdresse[] = [
  'declaree',
  'en_carence',
  'confirmee',
  'expiree',
  'remplacee',
  'desactivee',
  'annulee',
  'invalide',
];

export function etatAdresseAffiche(etat: string): string {
  return (LIBELLE_ETAT_ADRESSE as Record<string, string>)[etat] ?? etat;
}

export function tonAdresse(etat: string): 'ok' | 'attention' | 'neutre' {
  if (etat === 'confirmee') return 'ok';
  if (etat === 'declaree' || etat === 'en_carence' || etat === 'invalide') return 'attention';
  return 'neutre';
}

/** Le code se saisit sous la forme « XXXX-XXXX » (comme celui des clés : même alphabet). */
export const normaliserCodeAdresse = normaliserCodeVerification;
export const codeAdresseValide = codeVerificationValide;

/** Une déclaration attend son code ; une adresse en carence ou en attente se laisse annuler sans geste. */
export function attendUnCode(etat: string): boolean {
  return etat === 'declaree';
}
export function annulable(etat: string): boolean {
  return etat === 'declaree' || etat === 'en_carence';
}
export function desactivable(etat: string): boolean {
  return etat === 'confirmee';
}

/** Contrôle de forme minimal, avant l'envoi : l'API reste seule juge (ASCII strict, domaine déclaré du client). */
export function adresseBienFormee(adresse: string): boolean {
  const a = adresse.trim();
  return a.length >= 3 && a.length <= 254 && /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+$/.test(a);
}

export interface ResumeAdresse {
  ton: 'ok' | 'attention' | 'info';
  texte: string;
  /** Vrai : il faut qu'un administrateur déclare l'adresse (rien d'utilisable, et l'enrôlement l'exige). */
  demanderUnAdministrateur: boolean;
}

/** Ce que GET /v0/moi dit de l'adresse d'alerte, en une phrase. L'application n'invente rien : l'API reste seule juge. */
export function resumeAdresse(a: EtatAdresseAlerte | null | undefined, formaterDate: (iso: string) => string): ResumeAdresse | null {
  if (!a) return null;
  const enCarence = a.adresses.find((v) => v.etat === 'en_carence');
  if (a.suffisante) {
    const retenue = a.voie === 'fournisseur' ? a.adresse_fournisseur : a.adresses.find((v) => v.etat === 'confirmee')?.adresse;
    const origine =
      a.voie === 'fournisseur'
        ? 'fournie par votre fournisseur d’identité'
        : 'déclarée par un administrateur et confirmée par vous';
    const suite = enCarence
      ? ` Un changement vers ${enCarence.adresse} est en délai de sécurité${enCarence.active_le ? ` jusqu’au ${formaterDate(enCarence.active_le)}` : ''} : d’ici là, l’ancienne adresse reste destinataire de toutes vos alertes.`
      : '';
    return {
      ton: 'ok',
      texte: `Vos alertes de sécurité partent vers ${retenue ?? 'votre adresse'}, ${origine}.${suite}`,
      demanderUnAdministrateur: false,
    };
  }
  if (!a.exigee)
    return {
      ton: 'info',
      texte: 'Sur cette installation, une adresse d’alerte n’est pas exigée pour enregistrer une clé (démonstration ou développement).',
      demanderUnAdministrateur: false,
    };
  const attente = a.adresses.find((v) => v.etat === 'declaree');
  if (attente)
    return {
      ton: 'attention',
      texte: `Un administrateur a déclaré ${attente.adresse} pour vous. Elle n’est pas encore utilisable : saisissez le code reçu par courriel à cette adresse pour la confirmer.`,
      demanderUnAdministrateur: false,
    };
  if (enCarence)
    return {
      ton: 'attention',
      texte: `Votre adresse ${enCarence.adresse} est confirmée${enCarence.active_le ? `, elle sera utilisée à partir du ${formaterDate(enCarence.active_le)}` : ''}.`,
      demanderUnAdministrateur: false,
    };
  return {
    ton: 'attention',
    texte:
      'Aucune adresse d’alerte utilisable : l’enregistrement d’une clé de sécurité est refusé tant qu’Ekera ne sait pas où vous alerter. Demandez à un administrateur de déclarer votre adresse de courriel professionnelle.',
    demanderUnAdministrateur: true,
  };
}

/** Les déclarations en cours d'abord, puis les adresses en service, puis l'historique. */
export function ordonner(vues: readonly VueAdresse[]): VueAdresse[] {
  const rang = (e: string) => ETATS_ADRESSE.indexOf(e as EtatAdresse);
  return [...vues].sort((x, y) => rang(x.etat) - rang(y.etat));
}
